import { LeftOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
  AutoComplete,
  Button,
  Flex,
  Form,
  InputNumber,
  Space,
  Switch,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import ApprovalApi from 'apis/ApprovalApis';
import { PlantApi } from 'apis/PlantApis';
import { UserApi } from 'apis/UserApis';
import { EditIcon, PlusIcon, XIcon } from 'assets/svg';
import { ButtonAction, SelectForm } from 'components';
import TableComponent from 'components/Table/TableComponent';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import {
  IAddApprovalDetail,
  IApprovalDetail,
  IApprovalDetailTable,
  ICheckCreateApprovalDetail,
  IDeleteApprovalDetail,
  IGetApprovalDetail,
  IUpdateApprovalDetail,
  IUpdateStatusApprovalDetail,
} from 'interfaces/Approval';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import { IGetUserByPlantEmail } from 'interfaces/TeamSale/TeamSale';
import Content from 'layouts/Content';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import path from 'routes/Path';
import {
  formatDate,
  toastError,
  toastSuccess,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';

const { SaveTwoTone } = icons;

export interface IDetailSetApproval {
  approvalId: string;
  name: string;
}

const DetailSetApproval = () => {
  const { setTitle, setBreadcrumb } = useTitle();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const [form] = useForm();
  const [editIndex, setEditIndex] = useState(-1);
  const [isAdd, setIsAdd] = useState(false);
  const [plants, setPlants] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [data, setData] = useState<IApprovalDetailTable>({
    items: [],
    pageInfo: {
      currentPage: 1,
      hasNextPage: false,
      hasPreviousPage: false,
      itemsPerPage: 10,
      totalItems: 0,
      totalPages: 0,
    },
  });
  const defaultFilter = useMemo(() => {
    if (user?.isAdmin) return {};

    if (user?.isHO) return { plantId: user?.plantCode };

    return { plantND: user?.plantCode };
  }, [user?.isAdmin, user?.isHO, user?.plantCode]);

  const { approvalId, name } = (location.state || {}) as IDetailSetApproval;

  const columns: ColumnsTypeCustom = [
    {
      title: 'Plant',
      dataIndex: 'plantId',
      align: 'center',
      width: 100,
      render: (value, _, index) => {
        if (isAdd && !index)
          return (
            <Form.Item
              name="plant"
              style={{
                marginBottom: 0,
              }}
              initialValue={user?.isAdmin ? null : user?.plantCode}
            >
              <SelectForm
                allowClear={false}
                loading={getPlantsLoading}
                options={plants}
                size="middle"
                placeholder="Chọn chi nhánh"
                disabled={!user?.isAdmin}
              />
            </Form.Item>
          );

        return value;
      },
    },
    {
      title: 'Thứ tự',
      dataIndex: 'indexNum',
      align: 'right',
      width: 100,
      render: (value, _, index) => {
        if ((isAdd && !index) || index === editIndex)
          return (
            <Form.Item
              name="indexNum"
              style={{
                marginBottom: 0,
              }}
              initialValue={value}
            >
              <InputNumber min={1} placeholder="Nhập thứ tự" precision={0} />
            </Form.Item>
          );

        return value;
      },
    },
    {
      title: 'Người duyệt',
      dataIndex: ['tuser', 'email'],
      width: 200,
      render: (value, _, index) => {
        if ((isAdd && !index) || index === editIndex)
          return (
            <Form.Item
              name="email"
              style={{
                marginBottom: 0,
              }}
              initialValue={value}
            >
              <AutoComplete
                // disabled={!plantId && isAdd}
                allowClear
                size="middle"
                className="w-full rounded-xl"
                options={users}
                onSearch={handleSearchUsers}
                onSelect={handleSelectUser}
                onChange={handleChangeUser}
                placeholder="Nhập email người duyệt"
              />
            </Form.Item>
          );

        return value;
      },
    },
    {
      title: 'Plant ND',
      dataIndex: ['tuser', 'plantCode'],
      align: 'center',
      width: 150,
      render: (value, _, index) => {
        if ((isAdd && !index) || index === editIndex)
          return (
            <Form.Item
              name="plantND"
              style={{
                marginBottom: 0,
              }}
              initialValue={value}
            >
              <InputNumber disabled placeholder="Plant ND" />
            </Form.Item>
          );

        return value;
      },
    },
    {
      title: 'Người khai báo',
      dataIndex: ['tcreateUser', 'fullName'],
      width: 150,
    },
    {
      title: 'Ngày khai báo',
      dataIndex: 'createDate',
      width: 130,
      render: (value) => (value ? formatDate(value, 'HH:mm DD/MM/YYYY') : null),
    },
    {
      title: 'Người cập nhật',
      dataIndex: ['tmodifyUser', 'fullName'],
      width: 150,
    },
    {
      title: 'Ngày cập nhật',
      dataIndex: 'modifyDate',
      width: 130,
      render: (value) => (value ? formatDate(value, 'HH:mm DD/MM/YYYY') : null),
    },
    {
      title: 'Người on/off',
      dataIndex: ['tmodifyUser', 'fullName'],
      width: 150,
    },
    {
      title: 'Ngày on/off',
      dataIndex: 'modifyDate',
      width: 130,
      render: (value) => (value ? formatDate(value, 'HH:mm DD/MM/YYYY') : null),
    },
    {
      title: '',
      fixed: 'right',
      width: 150,
      align: 'center',
      render: (_, record, index) => {
        return (
          <Space size={'middle'} className="text-lg ">
            {(isAdd && !index) || index === editIndex ? (
              <>
                <ButtonAction tooltip="Lưu" onClick={handleSave}>
                  <SaveTwoTone style={{ fontSize: 20 }} />
                </ButtonAction>
                <ButtonAction tooltip="Huỷ" onClick={handleCancel}>
                  <XIcon />
                </ButtonAction>
              </>
            ) : (
              <>
                <ButtonAction
                  tooltip="Xoá"
                  disabled={isAdd || editIndex >= 0}
                  onClick={() =>
                    deleteApprovalDetail({
                      id: record.id,
                    })
                  }
                >
                  <XIcon />
                </ButtonAction>
                <ButtonAction
                  disabled={isAdd || editIndex >= 0}
                  tooltip="Chỉnh sửa"
                  onClick={() => handleClickEdit(index)}
                >
                  <EditIcon />
                  {/* {editIndex == -1 ?  : <EditGrayIcon />} */}
                </ButtonAction>
                {/* TODO */}
                <Switch
                  size="small"
                  value={record.useYn}
                  onChange={() => handleChangeStatus(record)}
                />
              </>
            )}
          </Space>
        );
      },
    },
  ];

  const { mutate: updateStatusApprovalDetail } = useMutation({
    mutationFn: (params: IUpdateStatusApprovalDetail) =>
      ApprovalApi.updateStatusApprovalDetail(params),
    onSuccess: () => {
      toastSuccess('Cập nhật trạng thái thành công');
      getApprovalDetail({
        approvalId,
        skip: 0,
        take: 100,
      });
    },
    onError: (error) => {
      console.log(error);
      toastError('Cập nhật trạng thái thất bại');
    },
  });

  const { mutateAsync: getUserByPlantEmail } = useMutation({
    mutationFn: (params: IGetUserByPlantEmail) =>
      UserApi.getUserByPlantEmail(params),
    onSuccess: (res) => {
      const items = res.data?.getUser?.items || [];

      setUsers(
        items.map((item: any) => ({
          label: item.email,
          value: item.email,
          id: item.id,
          plant: item.plantCode,
        })),
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutateAsync: checkCreateApprovalDetail } = useMutation({
    mutationFn: (params: ICheckCreateApprovalDetail) =>
      ApprovalApi.checkCreateApprovalDetail(params),
  });

  const { isPending: addApprovalPending, mutate: handleAddApproval } =
    useMutation({
      mutationFn: (params: IAddApprovalDetail) =>
        ApprovalApi.addApprovalDetail(params),
      onSuccess: () => {
        toastSuccess('Thêm người duyệt thành công');
        setIsAdd(false);
        form.resetFields();
        getApprovalDetail({
          approvalId,
          skip: 0,
          take: 100,
        });
        setUsers([]);
      },
      onError: (error) => {
        console.log(error);
        toastError('Thêm người duyệt thất bại');
      },
    });

  const { isPending, mutate: getApprovalDetail } = useMutation({
    mutationFn: (params: IGetApprovalDetail) =>
      ApprovalApi.getApprovalDetail({
        ...params,
        ...defaultFilter,
      }),
    onSuccess: (data) => {
      setData(data.data.getApprovalDetail);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { isPending: getPlantsLoading, mutate: getAllPlant } = useMutation({
    mutationFn: () => PlantApi.getAllPlantFilterHO(),
    onSuccess: (res: any) => {
      const plants = res.data?.getPlant.items || [];

      setPlants(
        plants.map((item: any) => ({
          label: item.id,
          value: item.id,
        })),
      );
    },
    onError: (error) => console.log(error),
  });

  const { mutate: deleteApprovalDetail } = useMutation({
    mutationFn: (params: IDeleteApprovalDetail) =>
      ApprovalApi.deleteApprovalDetail(params),
    onSuccess: () => {
      toastSuccess('Xoá người duyệt thành công');
      getApprovalDetail({
        approvalId,
        skip: 0,
        take: 100,
      });
    },
    onError: (error) => {
      console.log(error);
      toastError('Xoá người duyệt thất bại');
    },
  });

  const { mutate: updateApprovalDetail } = useMutation({
    mutationFn: (params: IUpdateApprovalDetail) =>
      ApprovalApi.updateApprovalDetail(params),
    onSuccess: () => {
      toastSuccess('Cập nhật người duyệt thành công');
      setEditIndex(-1);
      getApprovalDetail({
        approvalId,
        skip: 0,
        take: 100,
      });
      setUsers([]);
    },
    onError: (error) => {
      console.log(error);
      toastError('Cập nhật người duyệt thất bại');
    },
  });

  const handleBack = useCallback(() => navigate(path.SET_APPROVAL), [navigate]);

  const handleChangeStatus = async (record: any) => {
    const input: any = {};

    input.indexNum = record.indexNum;

    if (!record.useYn) {
      const result = await checkCreateApprovalDetail({
        ...input,
        approvalId,
      });

      if (result.data?.CheckInsertApprovalDetail?.status !== 200)
        return toastError(result.data?.CheckInsertApprovalDetail?.message);
    }

    updateStatusApprovalDetail({
      id: record.id,
      useYn: !record.useYn,
    });
  };

  const handleSearchUsers = (value: string) => {
    if (!value || value.length < 3) return setUsers([]);

    const plantCode = form.getFieldValue('plant');
    getUserByPlantEmail({
      plantCode,
      email: value,
    });
  };

  const handleSelectUser = (value: string) => {
    const user = users.find((item) => item.value === value);

    form.setFieldValue('plantND', user?.plant || null);
  };

  const handleChangeUser = (value: string) => {
    if (!value) form.setFieldValue('plantND', null);
  };

  const handleCancel = () => {
    if (isAdd) {
      setData((prev) => ({
        ...prev,
        items: prev.items.slice(1),
      }));
      setIsAdd(false);
    } else {
      setEditIndex(-1);
    }

    form.resetFields();
    setUsers([]);
  };

  const handleClickAdd = () => {
    if (isAdd) return;

    form.resetFields();
    if (!user?.isAdmin) {
      form.setFieldsValue({
        plant: user?.plantCode,
      });
    }
    setEditIndex(-1);
    setIsAdd(true);
    setData((prev) => ({
      ...prev,
      items: [
        {
          id: Date.now().toString(),
        } as IApprovalDetail,
        ...prev.items,
      ],
    }));
  };

  const handleSave = async () => {
    const values = form.getFieldsValue();

    const plantCode = values.plant;
    const email = values.email;
    const indexNum = values.indexNum;

    if (isAdd && !plantCode) return toastError('Vui lòng chọn chi nhánh');
    if (!indexNum) return toastError('Vui lòng nhập thứ tự');
    // if (data.items.length > 1 && indexNum % 2 !== 0 && editIndex !== -1)
    //   return toastError('Thứ tự phải là số chẵn');
    if (!email) return toastError('Vui lòng nhập email người duyệt');

    const res: any = await getUserByPlantEmail({
      plantCode,
      email,
    });
    const users = res.data?.getUser?.items || [];

    const userId = users.find((item: any) => item.email === email)?.id ?? '';

    if (!userId) return toastError('Người duyệt chưa có trên hệ thống');

    try {
      if (isAdd) {
        const result = await checkCreateApprovalDetail({
          approvalId,
          indexNum,
          userId,
        });

        if (result.data?.CheckInsertApprovalDetail?.status !== 200)
          return toastError(result.data?.CheckInsertApprovalDetail?.message);

        handleAddApproval({
          approvalId,
          indexNum,
          userId,
          plantId: plantCode,
        });
      } else {
        const approvalDetail = data.items[editIndex];

        const input: any = {};

        if (approvalDetail.userId !== userId) input.userId = userId;
        if (approvalDetail.indexNum !== indexNum) input.indexNum = indexNum;

        if (Object.keys(input).length !== 0) {
          const result = await checkCreateApprovalDetail({
            ...input,
            approvalId,
          });

          if (result.data?.CheckInsertApprovalDetail?.status !== 200)
            return toastError(result.data?.CheckInsertApprovalDetail?.message);
        }

        updateApprovalDetail({
          id: data.items[editIndex].id,
          indexNum,
          userId,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleClickEdit = (index: number) => {
    form.setFieldsValue({
      plant: data.items[index].plantId,
      indexNum: data.items[index].indexNum,
      email: data.items[index].tuser?.email,
    });

    setEditIndex(index);
    setIsAdd(false);
    if (isAdd)
      setData((prev) => ({
        ...prev,
        items: prev.items.slice(1),
      }));
  };

  useEffect(() => {
    if (!approvalId || !name) navigate(path.SET_APPROVAL);
    else {
      setTitle(name);
      setBreadcrumb([
        { title: 'Hệ thống' },
        { title: 'Thiết lập quy trình ký duyệt', onClick: handleBack },
        { title: name },
      ]);
    }
  }, [approvalId, handleBack, name, navigate, setBreadcrumb, setTitle]);

  useEffect(() => {
    getApprovalDetail({
      approvalId,
      skip: 0,
      take: 100,
    });
  }, [approvalId, getApprovalDetail]);

  useEffect(() => {
    getAllPlant();
  }, [getAllPlant]);

  useEffect(() => {
    if (!user?.isAdmin && user?.plantCode && isAdd) {
      form.setFieldsValue({
        plant: user?.plantCode,
      });
    }
  }, [form, isAdd, user?.isAdmin, user?.plantCode]);

  return (
    <div className="mt-4">
      <Button
        type="link"
        icon={<LeftOutlined className="w-5 h-5" />}
        style={{
          paddingInline: 0,
        }}
        onClick={handleBack}
      >
        Quay lại
      </Button>
      <Content>
        <Flex
          justify="flex-end"
          gap={6}
          style={{
            marginBottom: 9,
          }}
        >
          <Button
            disabled={editIndex >= 0}
            type="link"
            style={{
              paddingInline: 0,
            }}
            onClick={handleClickAdd}
          >
            Thêm người duyệt
          </Button>
          <Button
            disabled={editIndex >= 0}
            ghost
            type="primary"
            icon={<PlusIcon className="w-5 h-5" />}
            onClick={handleClickAdd}
          />
        </Flex>
        <Form form={form}>
          <TableComponent
            columns={columns}
            dataSource={data.items}
            //   pagination={{
            //     current: data.pageInfo?.currentPage,
            //     pageSize: data.pageInfo?.itemsPerPage,
            //     total: data.pageInfo?.totalItems,
            //     onChange: HocChangePagination(),
            //   }}
            loading={isPending || addApprovalPending}
            pagination={false}
          />
        </Form>
      </Content>
    </div>
  );
};

export default DetailSetApproval;
